/**
 * One signal that aborts when any of `signals` does, carrying that signal's reason (TENANT-5664:
 * the booking card's per-request cancel combined with the live quote's settle-point deadline).
 *
 * `AbortSignal.any` is used where the browser has it; the browserslist target ("last 2
 * versions") still includes browsers without it, so those get the same behaviour from a
 * hand-built controller.
 *
 * @param {AbortSignal[]} signals
 * @param {Object} [options]
 * @param {((signals: AbortSignal[]) => AbortSignal)|undefined} [options.any] - `AbortSignal.any`;
 *   injectable so both paths are unit-tested
 * @returns {AbortSignal}
 */
export const combineAbortSignals = (signals, {
    any = globalThis.AbortSignal?.any?.bind(globalThis.AbortSignal),
} = {}) => {
    if (!Array.isArray(signals) || signals.length === 0 || !signals.every((signal) => signal instanceof AbortSignal)) {
        throw new TypeError('combineAbortSignals: needs a non-empty array of AbortSignals.')
    }
    if (typeof any === 'function') return any(signals)

    const combined = new AbortController()
    const alreadyAborted = signals.find((signal) => signal.aborted)
    if (alreadyAborted) {
        combined.abort(alreadyAborted.reason)
        return combined.signal
    }
    const listeners = signals.map((signal) => {
        const onAbort = () => {
            combined.abort(signal.reason)
            for (const [
                source,
                listener,
            ] of listeners) source.removeEventListener('abort', listener)
        }
        signal.addEventListener('abort', onAbort, {
            once: true,
        })
        return [
            signal,
            onAbort,
        ]
    })
    return combined.signal
}
